import { useMemo } from 'react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import { Pagination } from '@/components/data-display/Pagination';
import { Badge } from '@/components/data-display/Badge';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { usePricing } from './usePricing';
import { PricingEditor } from './PricingEditor';
import type { ClientPricingListItem } from './types';
import { paymentServiceLabel } from './types';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDecimal6(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.000000';
  return num.toFixed(6);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '\u2014';
  }
}

// ---------------------------------------------------------------------------
// Sortable column IDs — must mirror the backend ListPricingQueryDto whitelist.
// `updated_by` is intentionally absent: the column is not on
// convera.client_pricing and operator attribution is out of scope. The
// incoming fee columns are shown but not sortable — the backend orders on the
// outgoing terms only, and offering a header that silently does nothing is
// worse than a plain one.
// ---------------------------------------------------------------------------

const SORTABLE_COLUMNS = new Set([
  'company_name',
  'payment_service_code',
  'outgoing_fee_fixed_value',
  'outgoing_fee_percent_value',
  'updated_at',
]);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PricingPage() {
  const {
    data,
    isLoading,
    error,
    filters,
    setSearch,
    setSorting,
    setPage,
    setPageSize,
    selectedClientId,
    selectedServiceCode,
    pricingDetail,
    detailLoading,
    detailError,
    openEditor,
    closeEditor,
    updatePricing,
    isUpdating,
  } = usePricing();

  const columns = useMemo<ColumnDef<ClientPricingListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'company_name',
        header: 'Company Name',
        enableSorting: true,
        cell: ({ row }) => (
          <span
            className="inline-block max-w-[250px] truncate font-semibold text-[#333]"
            title={row.original.company_name}
          >
            {row.original.company_name}
          </span>
        ),
      },
      {
        accessorKey: 'payment_service_code',
        header: 'Service',
        enableSorting: true,
        cell: ({ row }) => (
          <Badge variant={row.original.payment_service_code === 'SWIFT' ? 'info' : 'warning'}>
            {paymentServiceLabel(row.original.payment_service_code)}
          </Badge>
        ),
      },
      {
        accessorKey: 'outgoing_fee_fixed_value',
        header: 'Out. fixed',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#333]">
            ${formatDecimal6(row.original.outgoing_fee_fixed_value)}
          </span>
        ),
      },
      {
        accessorKey: 'outgoing_fee_percent_value',
        header: 'Out. %',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#333]">
            {formatDecimal6(row.original.outgoing_fee_percent_value)}%
          </span>
        ),
      },
      {
        accessorKey: 'incoming_fee_fixed_value',
        header: 'In. fixed',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#333]">
            ${formatDecimal6(row.original.incoming_fee_fixed_value)}
          </span>
        ),
      },
      {
        accessorKey: 'incoming_fee_percent_value',
        header: 'In. %',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#333]">
            {formatDecimal6(row.original.incoming_fee_percent_value)}%
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#666]">{formatDate(row.original.updated_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-right">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openEditor(row.original.client_id, row.original.payment_service_code);
              }}
              className="border-[#6B0F1A] text-[#6B0F1A] hover:bg-[#6B0F1A] hover:text-white"
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [openEditor],
  );

  // Map filter state to TanStack sorting state
  const sorting: SortingState = [{ id: filters.sort_by, desc: filters.sort_order === 'desc' }];

  const handleSortingChange = (newSorting: SortingState) => {
    if (newSorting.length > 0) {
      const col = newSorting[0];
      if (SORTABLE_COLUMNS.has(col.id)) {
        setSorting(col.id, col.desc ? 'desc' : 'asc');
      }
    }
  };

  const items = data?.items ?? [];
  const totalRows = data?.total ?? 0;

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        {/* Header — matches legacy pricing-header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[22px] font-bold text-[#333]">Pricing</h1>
            <p className="mt-0.5 text-[13px] text-[#999]">
              Fees applied to each client, service by service
            </p>
          </div>
          <div className="w-[340px]">
            <Input
              placeholder="Search clients..."
              value={filters.search}
              onChange={setSearch}
              aria-label="Search pricing by company name"
            />
          </div>
        </div>

        {/* Error state */}
        {error && !isLoading && (
          <div
            className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
            role="alert"
          >
            Failed to load pricing. Please try again.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && items.length === 0 && (
          <div className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-12 text-center text-sm text-[#999]">
            No pricing configurations found
          </div>
        )}

        {/* Table */}
        {(isLoading || items.length > 0) && (
          <>
            <DataTable<ClientPricingListItem>
              columns={columns}
              data={items}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              isLoading={isLoading}
            />
            <Pagination
              pageIndex={filters.page}
              pageSize={filters.page_size}
              totalRows={totalRows}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}

        {/* Editor modal */}
        <PricingEditor
          isOpen={selectedClientId !== null}
          pricing={pricingDetail}
          serviceCode={selectedServiceCode}
          loading={detailLoading}
          isUpdating={isUpdating}
          onSave={updatePricing}
          onClose={closeEditor}
        />

        {/* Detail error — show toast inline if detail load fails */}
        {detailError && selectedClientId && (
          <div className="sr-only" role="alert">
            Failed to load client pricing detail.
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
